import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Button,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import {
  DirectionsBike,
  Schedule,
  CheckCircle,
  Warning,
  Refresh
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import stravaApiService from '../services/stravaApi.js';
import settingsService from '../services/settingsService.js';

const WaxTracking = () => {
  const { user, loginWithRedirect } = useAuth0();
  const [waxData, setWaxData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {    
    // Always try to load wax data when settings change
    // The settings service should have provided defaults
    if (Object.keys(settings).length > 0) {
      console.log('WaxTracking settings:', settings);
      console.log('Settings object keys:', Object.keys(settings));
      console.log('Settings values:', {
        match_pattern: settings.match_pattern,
        wax_interval_distance: settings.wax_interval_distance,
        gear_id: settings.gear_id
      });
      console.log('Loading wax data with settings:', settings);
      loadWaxData();
    }
  }, [settings]);

  const loadSettings = () => {
    const userSettings = settingsService.getAllSettings();
    console.log('Loaded settings from service:', userSettings);
    setSettings(userSettings);
  };

  const loadWaxData = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    
    try {
      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cachedData = localStorage.getItem('wax_tracking_cache');
        if (cachedData) {
          const { data, timestamp } = JSON.parse(cachedData);
          
          // Check if cache is from today (same date)
          const cacheDate = new Date(timestamp).toDateString();
          const todayDate = new Date().toDateString();
          
          if (cacheDate === todayDate) {
            console.log('Using cached wax tracking data from today');
            setWaxData(data);
            setLoading(false);
            return;
          } else {
            console.log('Cache is from a different day, fetching fresh data');
            localStorage.removeItem('wax_tracking_cache');
          }
        }
      }
      
      // Get the Strava access token from the nickname field
      // The profile fetch script stores it there
      const stravaAccessToken = user?.nickname;
      
      if (!stravaAccessToken) {
        throw new Error('Strava access token not found in user profile. Please re-authenticate with Strava.');
      }
      
      console.log('Fetching fresh wax data from Strava API...');
      
      // Map settings to the format expected by the Strava API service
      const apiSettings = {
        matchPattern: settings.match_pattern,
        waxIntervalDistance: settings.wax_interval_distance,
        gearId: settings.gear_id
      };
      
      console.log('API settings:', apiSettings);
      
      // Get real data from Strava API using the Strava access token
      const waxData = await stravaApiService.getWaxTrackingData(stravaAccessToken, apiSettings, forceRefresh);
      console.log('Got wax data from API:', waxData);
      
      // Cache the response with timestamp
      const cacheData = {
        data: waxData,
        timestamp: Date.now()
      };
      localStorage.setItem('wax_tracking_cache', JSON.stringify(cacheData));
      console.log('Cached wax tracking data');
      
      setWaxData(waxData);
    } catch (err) {
      console.error('Error loading wax data:', err);
      
      // If Strava token is expired/invalid/missing or unauthorized, redirect to login to restart flow
      const isAuthIssue = (
        typeof err?.message === 'string' && (
          err.message.includes('expired') ||
          err.message.includes('invalid') ||
          err.message.includes('401') ||
          err.message.toLowerCase().includes('token not found')
        )
      );

      if (isAuthIssue) {
        try {
          // Clear local caches before re-auth
          localStorage.removeItem('last_wax_date');
          localStorage.removeItem('wax_tracking_cache');

          await loginWithRedirect({
            authorizationParams: {
              connection: 'Strava',
              prompt: 'login',
              redirect_uri: `${window.location.origin}/callback`
            }
          });
          return;
        } catch (redirectError) {
          console.error('Failed to redirect for re-authentication:', redirectError);
        }
      }

      setError(`Failed to load wax data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = () => {
    if (!waxData) return 'default';
    if (waxData.needsWax) return 'error';
    
    // Show warning if 90% or more of the wax interval has been reached
    const progressPercentage = (waxData.distanceSinceWax / settings.wax_interval_distance) * 100;
    if (progressPercentage >= 90) return 'warning';
    
    return 'success';
  };

  const getStatusIcon = () => {
    if (!waxData) return <DirectionsBike />;
    if (waxData.needsWax) return <Warning />;
    
    // Show schedule icon if 90% or more of the wax interval has been reached
    const progressPercentage = (waxData.distanceSinceWax / settings.wax_interval_distance) * 100;
    if (progressPercentage >= 90) return <Schedule />;
    
    return <CheckCircle />;
  };

  const getProgressValue = () => {
    if (!waxData || !waxData.hasLastWax) return 0;
    return Math.min((waxData.distanceSinceWax / settings.wax_interval_distance) * 100, 100);
  };

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading Wax Tracking Data...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Fetching your Strava activities
        </Typography>
      </Paper>
    );
  }

  if (error) {
    const needsReauth = error.includes('expired') || error.includes('invalid') || error.includes('401');
    
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6">Error Loading Wax Data</Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
        <Box display="flex" gap={2}>
          <Button 
            variant="contained" 
            onClick={loadWaxData}
            startIcon={<Refresh />}
          >
            Retry
          </Button>
          {needsReauth && (
            <Button 
              variant="outlined" 
              onClick={() => window.location.reload()}
              startIcon={<Refresh />}
            >
              Re-authenticate
            </Button>
          )}
        </Box>
      </Paper>
    );
  }

  if (!waxData) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <DirectionsBike sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h5">Chain Wax Tracking</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Loading wax tracking data...
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <DirectionsBike sx={{ mr: 2, color: 'primary.main' }} />
        <Typography variant="h5">Chain Wax Tracking</Typography>
      </Box>

      {!waxData.hasLastWax ? (
        <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Start Tracking Your Chain Maintenance
            </Typography>
            <Typography variant="body2">
              {waxData.message}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Settings:</strong> Pattern "{settings.match_pattern}", Interval {settings.wax_interval_distance}km
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {/* Status Card */}
          <Card sx={{ mb: 3, bgcolor: waxData.needsWax ? 'error.light' : 'success.light' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  {getStatusIcon()}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {waxData.needsWax ? 'Whacks Off' : 'Whacks On'}
                  </Typography>
                </Box>
                <Chip 
                  label={waxData.needsWax 
                    ? 'Wax Overdue' 
                    : (() => {
                        const progressPercentage = (waxData.distanceSinceWax / settings.wax_interval_distance) * 100;
                        return progressPercentage >= 90 ? 'Wax Due Soon' : 'All Good';
                      })()
                  } 
                  color={getStatusColor()}
                  size="small"
                />
              </Box>

              {/* Progress Bar */}
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">
                    Distance since last wax: {waxData.distanceSinceWax}km
                  </Typography>
                  <Typography variant="body2">
                    {settings.wax_interval_distance}km between waxes
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={getProgressValue()} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              {/* Status Message */}
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {waxData.needsWax 
                  ? `You need to wax your chain! (${waxData.distanceSinceWax - settings.wax_interval_distance}km overdue)`
                  : `Next wax in ${waxData.distanceToWax}km`
                }
              </Typography>
            </CardContent>
          </Card>

          {/* Details */}
          <Box display="flex" gap={2} flexWrap="wrap">
            <Card variant="outlined" sx={{ flex: 1, minWidth: 200 }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  Last Wax
                </Typography>
                <Typography variant="body2">
                  {new Date(waxData.lastWaxDate).toLocaleDateString()}
                </Typography>
                       {waxData.lastWaxActivityId ? (
                         <Typography 
                           variant="caption" 
                           component="a"
                           href={`https://www.strava.com/activities/${waxData.lastWaxActivityId}`}
                           target="_blank"
                           rel="noopener noreferrer"
                           sx={{ 
                             color: '#FC5200',
                             textDecoration: 'underline',
                             cursor: 'pointer',
                             fontWeight: 'bold',
                             '&:hover': {
                               color: '#E54700'
                             }
                           }}
                         >
                           View on Strava
                         </Typography>
                       ) : (
                         <Typography variant="caption" color="text.secondary">
                           {waxData.lastWaxActivity}
                         </Typography>
                       )}
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ flex: 1, minWidth: 200 }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  Rides Since Last Wax Date
                </Typography>
                <Typography variant="h4" color="primary">
                  {waxData.activitiesCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  rides tracked
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <Button 
              variant="outlined" 
              onClick={() => {
                // Clear all caches and force refresh
                localStorage.removeItem('last_wax_date');
                localStorage.removeItem('wax_tracking_cache');
                console.log('Refresh: cleared all caches and forcing refresh');
                loadWaxData(true);
              }}
              startIcon={<Refresh />}
              size="small"
            >
              Refresh
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default WaxTracking;
